/**
 * Pure helpers used by `ActiveThreadsSection` so the side-effect-free
 * grouping rules can be unit-tested in isolation.
 *
 * Two helpers live here:
 *  - {@link pickLooseThreads} — identify threads whose project no longer
 *    exists in the projects store so the sidebar can surface them under a
 *    "Loose threads" sub-row inside the Projects group instead of dropping
 *    them on the floor.
 *  - {@link computeAllProjectsCollapsed} — derive whether every project in a
 *    given list is collapsed, used to drive the Projects group's
 *    "collapse all / expand all" toggle.
 */
/**
 * Minimal structural inputs — kept independent of the branded
 * `SidebarThreadSummary` / `Project` types so this module stays trivially
 * testable without having to forge brand intersections.
 */
import {
  buildWorkerSubRows,
  computeMasterAttentionCount,
  type WorkerSubRowItem,
  type WorkerWithParent,
} from "./sidebarWorkerRows.logic";
import type { ForgeRuntimeSurface } from "../../environments/runtimeSurface";

/**
 * The "This Device" sidebar group represents the user's *local machine* — its
 * filesystem projects, terminal, git, etc. On the official cloud web surface
 * (`cloud-official`, i.e. cloud.forge.cmmd.ai) the user is a browser talking to
 * cloud runtimes; there is no local device, so the group is pointless chrome
 * (it can only surface the cloud master's own non-cloud env). Hide it there.
 * Every other surface has a real local environment worth showing:
 *   - `desktop-local` / `desktop-cloud` — the Electron app runs on a machine
 *   - `local-web-dev` — the dev web server's local projects
 */
export function shouldShowLocalEnvironmentGroup(surface: ForgeRuntimeSurface): boolean {
  return surface !== "cloud-official";
}

export interface ProjectIdentity {
  readonly environmentId: string;
  readonly id: string;
  readonly memberProjectRefs?: readonly {
    readonly environmentId: string;
    readonly projectId: string;
  }[];
}

export interface ThreadProjectAnchor {
  readonly environmentId: string;
  readonly projectId: string;
}

export interface SidebarThreadSectionInput extends ThreadProjectAnchor {
  readonly id: string;
  readonly title?: string | undefined;
  readonly kind?: string | null | undefined;
  readonly status?: string | null | undefined;
  readonly parentThreadId?: string | null | undefined;
  readonly workerRole?: string | null | undefined;
  readonly createdAt?: string | undefined;
  readonly updatedAt?: string | undefined;
  readonly latestUserMessageAt?: string | null | undefined;
  readonly messages?: readonly { readonly createdAt: string; readonly role: string }[];
}

export type ThreadWorkspaceResolver<TThread> = (thread: TThread) => string | null;

export interface CollapseStateProject {
  readonly projectKey: string;
}

export interface ActiveThreadSectionModel<TThread> {
  readonly pinnedThreads: readonly TThread[];
  readonly pinnedThreadKeys: readonly string[];
  readonly excludedFromProjectsThreadKeys: ReadonlySet<string>;
  readonly knownProjectKeys: ReadonlySet<string>;
  readonly looseThreads: readonly TThread[];
  readonly allLocalProjectsCollapsed: boolean;
  readonly projectCollisionSubLabels: ReadonlyMap<string, string>;
  readonly workersByMasterThreadKey: ReadonlyMap<
    string,
    {
      readonly workers: readonly WorkerSubRowItem[];
      readonly attention: number;
      readonly environmentId: string;
    }
  >;
}

/**
 * Build the set of "physical" project keys (`${environmentId}:${id}`) that
 * the sidebar knows about. Threads referencing a project key outside this
 * set are loose threads.
 */
export function buildKnownProjectKeySet(projects: readonly ProjectIdentity[]): ReadonlySet<string> {
  const projectKeys = new Set<string>();
  for (const project of projects) {
    projectKeys.add(`${project.environmentId}:${project.id}`);
    for (const member of project.memberProjectRefs ?? []) {
      projectKeys.add(`${member.environmentId}:${member.projectId}`);
    }
  }
  return projectKeys;
}

function buildScopedThreadKey(thread: {
  readonly environmentId: string;
  readonly id: string;
}): string {
  return `${thread.environmentId}:${thread.id}`;
}

/**
 * Return the visible threads whose `(environmentId, projectId)` does not
 * resolve to a known project. These threads previously surfaced inside the
 * synthetic "Unscoped" workspace; the new sidebar groups them under a
 * "Loose threads" sub-row inside the Projects group instead.
 *
 * Generic over the thread shape so callers can preserve their concrete
 * branded types (e.g. `SidebarThreadSummary`) at the call site without
 * casting back from a wider type here.
 */
export function pickLooseThreads<TThread extends ThreadProjectAnchor>(
  visibleThreads: readonly TThread[],
  knownProjectKeys: ReadonlySet<string>,
  resolveThreadWorkspaceKey?: ThreadWorkspaceResolver<TThread>,
): readonly TThread[] {
  return visibleThreads.filter((thread) => {
    if (resolveThreadWorkspaceKey?.(thread)) return false;
    return !knownProjectKeys.has(`${thread.environmentId}:${thread.projectId}`);
  });
}

/**
 * Returns `true` when at least one project is present and every one of them
 * is collapsed. The empty-list case returns `false` so the toggle never
 * flips into "expand all" mode when there's nothing to expand.
 */
export function computeAllProjectsCollapsed(
  projects: readonly CollapseStateProject[],
  projectExpandedById: Readonly<Record<string, boolean>>,
): boolean {
  if (projects.length === 0) return false;
  return projects.every((project) => (projectExpandedById[project.projectKey] ?? true) === false);
}

/**
 * Minimal shape needed to compute name-collision disambiguators. Kept
 * structural so callers can pass branded `SidebarProjectSnapshot` instances
 * without converting first.
 */
export interface ProjectCollisionInput {
  readonly projectKey: string;
  readonly name: string;
  readonly cwd: string;
}

function splitParentSegments(cwd: string): readonly string[] {
  if (!cwd) return [];
  // Strip trailing slashes and an optional leading slash so we get a flat
  // list of meaningful segments. We then drop the final segment because
  // it's the project's own folder name (already shown as the project label).
  const trimmed = cwd.replace(/[\\/]+$/, "");
  const segments = trimmed.split(/[\\/]+/).filter((segment) => segment.length > 0);
  return segments.slice(0, -1);
}

/**
 * Build a map of `projectKey → disambiguating-path` for projects whose
 * `name` collides with another project in the same list. The label is the
 * shortest tail of cwd parent segments that remains unique within the
 * collision group, with redundant trailing segments (those that every peer
 * shares at the same leaf-distance) stripped so the label stays signal-only.
 *
 * The result is prefixed with `…/` when it doesn't reach the filesystem
 * root, or `/` when it does (so there's nothing left to elide above it).
 *
 * Projects with unique names are intentionally absent from the map so
 * non-colliding rows can stay clean.
 *
 * Examples — given two projects both named `server`:
 *   - `/Users/me/.codex/worktrees/0e93/cmmd-forge/apps/server`
 *   - `/Users/me/Documents/Projects/CMMD/.worktrees/cmmd-forge/x/apps/server`
 *
 * Both share `apps` (and `cmmd-forge` immediately above) as trailing noise,
 * so the disambiguator collapses to just the first divergent segment:
 * `…/0e93` vs `…/x`. The label is "show the user the smallest thing that
 * tells these rows apart", not "show the full unique suffix".
 */
export function buildProjectNameCollisionSubLabels(
  projects: readonly ProjectCollisionInput[],
): ReadonlyMap<string, string> {
  const result = new Map<string, string>();

  const groups = new Map<string, ProjectCollisionInput[]>();
  for (const project of projects) {
    const list = groups.get(project.name) ?? [];
    list.push(project);
    groups.set(project.name, list);
  }

  for (const group of groups.values()) {
    if (group.length < 2) continue;

    const parentSegmentsByKey = new Map<string, readonly string[]>();
    for (const project of group) {
      parentSegmentsByKey.set(project.projectKey, splitParentSegments(project.cwd));
    }

    for (const project of group) {
      const ownSegments = parentSegmentsByKey.get(project.projectKey) ?? [];
      if (ownSegments.length === 0) {
        // No parent context to show — leave this row label-less rather than
        // emitting a confusing empty sub-label.
        continue;
      }

      // Phase 1 — find the shortest contiguous leaf-anchored suffix whose
      // joined string is unique within the collision group. This is what
      // mathematically separates the rows.
      let suffixLen: number | null = null;
      for (let candidateLen = 1; candidateLen <= ownSegments.length; candidateLen++) {
        const candidate = ownSegments.slice(-candidateLen).join("/");
        const isUnique = group.every((other) => {
          if (other.projectKey === project.projectKey) return true;
          const otherSegments = parentSegmentsByKey.get(other.projectKey) ?? [];
          if (otherSegments.length < candidateLen) return true;
          return otherSegments.slice(-candidateLen).join("/") !== candidate;
        });
        if (isUnique) {
          suffixLen = candidateLen;
          break;
        }
      }

      if (suffixLen === null) {
        // Fallback: the cwds were truly identical for this group (shouldn't
        // happen in practice — that's a dedup bug, not a disambiguation
        // case). Show the full parent path so the user can still eyeball
        // any difference rather than dropping the row from the map.
        result.set(project.projectKey, `/${ownSegments.join("/")}`);
        continue;
      }

      // Phase 2 — strip trailing segments (closest to the leaf) that every
      // peer shares at the same leaf-distance. These add no information:
      // they're already implied by the shared project name + neighbouring
      // structure. Stop the moment we hit a segment that actually diverges
      // from at least one peer.
      let trimmedLen = suffixLen;
      while (trimmedLen > 1) {
        const leafDistance = suffixLen - trimmedLen;
        const myLeafSeg = ownSegments[ownSegments.length - 1 - leafDistance];
        const sharedWithEveryPeer = group.every((other) => {
          if (other.projectKey === project.projectKey) return true;
          const otherSegments = parentSegmentsByKey.get(other.projectKey) ?? [];
          if (otherSegments.length < leafDistance + 1) return false;
          return otherSegments[otherSegments.length - 1 - leafDistance] === myLeafSeg;
        });
        if (!sharedWithEveryPeer) break;
        trimmedLen -= 1;
      }

      const trimmedStart = ownSegments.length - suffixLen;
      const trimmedEnd = trimmedStart + trimmedLen;
      const trimmedSegments = ownSegments.slice(trimmedStart, trimmedEnd);
      const reachedRoot = trimmedStart === 0;
      const joined = trimmedSegments.join("/");
      result.set(project.projectKey, reachedRoot ? `/${joined}` : `…/${joined}`);
    }
  }

  return result;
}

function getThreadSortTimestamp(
  thread: SidebarThreadSectionInput,
  sortOrder: "updated_at" | "created_at",
): number {
  if (sortOrder === "created_at") return Date.parse(thread.createdAt ?? "") || 0;
  if (thread.latestUserMessageAt) return Date.parse(thread.latestUserMessageAt) || 0;
  let latestUserMessageTimestamp = 0;
  for (const message of thread.messages ?? []) {
    if (message.role !== "user") continue;
    latestUserMessageTimestamp = Math.max(
      latestUserMessageTimestamp,
      Date.parse(message.createdAt) || 0,
    );
  }
  return latestUserMessageTimestamp || Date.parse(thread.updatedAt ?? thread.createdAt ?? "") || 0;
}

/**
 * One sort rule for every sidebar section, in both layouts. Exported so the
 * Activity layout orders its flat list identically to the per-project lists
 * rather than reimplementing the tie-break.
 */
export function sortSectionThreads<TThread extends SidebarThreadSectionInput>(
  threads: readonly TThread[],
  sortOrder: "updated_at" | "created_at",
): TThread[] {
  return threads.toSorted((left, right) => {
    const rightTimestamp = getThreadSortTimestamp(right, sortOrder);
    const leftTimestamp = getThreadSortTimestamp(left, sortOrder);
    if (rightTimestamp !== leftTimestamp) return rightTimestamp > leftTimestamp ? 1 : -1;
    return right.id.localeCompare(left.id);
  });
}

function buildWorkerRows<TThread extends SidebarThreadSectionInput>(
  sidebarThreads: readonly TThread[],
): ReadonlyMap<
  string,
  {
    readonly workers: readonly WorkerSubRowItem[];
    readonly attention: number;
    readonly environmentId: string;
  }
> {
  const workers: WorkerWithParent[] = sidebarThreads
    .filter((thread) => thread.kind === "worker")
    .map((thread) => ({
      id: thread.id,
      title: thread.title ?? thread.id,
      kind: "worker",
      status: (thread.status ?? "idle") as WorkerWithParent["status"],
      parentThreadId: thread.parentThreadId ?? null,
      workerRole: (thread.workerRole ?? null) as Exclude<WorkerWithParent["workerRole"], undefined>,
    }));

  const map = new Map<
    string,
    {
      readonly workers: readonly WorkerSubRowItem[];
      readonly attention: number;
      readonly environmentId: string;
    }
  >();
  for (const thread of sidebarThreads) {
    if (thread.kind !== "master") continue;
    const workersForMaster = buildWorkerSubRows(workers, thread.id);
    if (workersForMaster.length === 0) continue;
    map.set(buildScopedThreadKey(thread), {
      workers: workersForMaster,
      attention: computeMasterAttentionCount(workersForMaster),
      environmentId: thread.environmentId,
    });
  }
  return map;
}

export function buildActiveThreadSectionModel<TThread extends SidebarThreadSectionInput>(input: {
  readonly allProjects: readonly ProjectIdentity[];
  readonly visibleSidebarThreads: readonly TThread[];
  readonly sidebarThreads: readonly SidebarThreadSectionInput[];
  readonly pinnedThreadById: Readonly<Record<string, boolean>>;
  readonly threadSortOrder: "updated_at" | "created_at";
  readonly sortedProjects: readonly ProjectCollisionInput[];
  readonly projectExpandedById: Readonly<Record<string, boolean>>;
}): ActiveThreadSectionModel<TThread> {
  const pinnedThreads = sortSectionThreads(
    input.visibleSidebarThreads.filter(
      (thread) => input.pinnedThreadById[buildScopedThreadKey(thread)] === true,
    ),
    input.threadSortOrder,
  );
  const pinnedThreadKeys = pinnedThreads.map(buildScopedThreadKey);
  const knownProjectKeys = buildKnownProjectKeySet(input.allProjects);
  return {
    pinnedThreads,
    pinnedThreadKeys,
    excludedFromProjectsThreadKeys: new Set(pinnedThreadKeys),
    knownProjectKeys,
    looseThreads: pickLooseThreads(input.visibleSidebarThreads, knownProjectKeys),
    allLocalProjectsCollapsed: computeAllProjectsCollapsed(
      input.sortedProjects,
      input.projectExpandedById,
    ),
    projectCollisionSubLabels: buildProjectNameCollisionSubLabels(input.sortedProjects),
    workersByMasterThreadKey: buildWorkerRows(input.sidebarThreads),
  };
}
